import { Transaction, EditorState } from 'prosemirror-state'
import { RemoveMarkStep } from 'prosemirror-transform'
import dayjs from 'dayjs'
import { TRACKABLE_MARKS } from './utils'
import { ourUuid } from 'utils'
import { Context } from './Types'

/**
 * Wrap a RemoveMarkStep and keep track of which marks are removed from the changed
 * range. We keep track of the removed marks in the array `before` of the
 * `formatting_change` mark.
 *
 * @param step - the step to track
 * @param userId - the id of the user making the change
 * @param state - the state of the editor
 * @param tr - the wrapped transaction. NOTE: This is not the original transaction
 */
export function trackRemoveMarkStep(
  step: RemoveMarkStep,
  userId: number,
  state: EditorState,
  tr: Transaction,
  context?: Context,
) {
  tr.doc.nodesBetween(step.from, step.to, (node, pos) => {
    // Block nodes do not support marks
    if (!node.isInline) {
      return true
    }

    const start = Math.max(step.from, pos)
    const end = Math.min(step.to, pos + node.nodeSize)
    const removedMarkName = step.mark.type.name

    // Ignore tracked deletions
    if (node.marks.find((mark) => mark.type === state.schema.marks.deletion)) {
      return false
    }

    tr.removeMark(start, end, step.mark)

    if (TRACKABLE_MARKS.includes(removedMarkName) && node.marks.find(({ type }) => type.name === removedMarkName)) {
      const formattingChangeMark = node.marks.find(({ type }) => type === state.schema.marks.formatting_change)
      const createdAt = formattingChangeMark ? formattingChangeMark.attrs.createdAt : dayjs().toISOString()
      const uuid = context?.uuid || formattingChangeMark?.attrs.uuid || ourUuid()

      let after: any
      let before: any

      if (formattingChangeMark) {
        const { after: currentAfter, before: currentBefore } = formattingChangeMark.attrs

        if (currentAfter.includes(removedMarkName)) {
          after = currentAfter.filter((markName: string) => markName !== removedMarkName)
          before = currentBefore
        } else {
          after = currentAfter
          before = currentBefore.includes(removedMarkName) ? currentBefore : currentBefore.concat(removedMarkName)
        }
      } else {
        after = []
        before = [removedMarkName]
      }

      if (after.length || before.length) {
        tr.addMark(start, end, state.schema.marks.formatting_change.create({ userId, createdAt, before, after, uuid }))
      } else if (formattingChangeMark) {
        tr.removeMark(start, end, formattingChangeMark)
      }
    }
  })
}
